import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, 
  X, 
  Package, 
  User, 
  Building2, 
  Layers,
  AlertTriangle,
  TrendingUp,
  FileText,
  Clock,
  Palmtree,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { Input } from './ui/input';
import { Dialog, DialogContent } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIPO_CONFIG = {
  of: { icon: Package, color: 'bg-blue-500', label: 'OF' },
  operador: { icon: User, color: 'bg-green-500', label: 'Operador' },
  marca: { icon: Building2, color: 'bg-purple-500', label: 'Marca' },
  operacao: { icon: Layers, color: 'bg-amber-500', label: 'Operação' },
  alerta: { icon: AlertTriangle, color: 'bg-red-500', label: 'Alerta' },
  status: { icon: TrendingUp, color: 'bg-emerald-500', label: 'Estado' },
  ausencia: { icon: Palmtree, color: 'bg-cyan-500', label: 'Ausência' },
  link: { icon: FileText, color: 'bg-stone-500', label: 'Página' }
};

export default function BuscaGlobal({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  
  // Debounce para evitar muitas chamadas
  const debounceRef = useRef(null);
  
  const buscar = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResultados([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/busca`, {
        params: { q: searchQuery, limit: 15 }
      });
      setResultados(response.data.resultados || []);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Erro na busca:', error);
      setResultados([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      buscar(query);
    }, 300);
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, buscar]);
  
  // Focus input quando abre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);
  
  // Reset quando fecha
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResultados([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);
  
  // Navegar para resultado
  const irParaResultado = (resultado) => {
    if (resultado.link) {
      navigate(resultado.link);
      onClose();
    }
  };
  
  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, resultados.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && resultados[selectedIndex]) {
      e.preventDefault();
      irParaResultado(resultados[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };
  
  // Sugestões de pesquisa
  const sugestoes = [
    'OFs em atraso',
    'Estado da fábrica',
    'Funcionários ausentes',
    'Performance da equipa',
    'Relatórios'
  ];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden" data-testid="busca-global-dialog">
        {/* Search Input - Mais largo e compacto */}
        <div className="p-3 border-b bg-white sticky top-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              ref={inputRef}
              placeholder="Pesquisar OFs, operadores, clientes..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-10 pr-10 py-2 h-10 text-sm border-0 shadow-none focus-visible:ring-0"
              data-testid="busca-global-input"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 animate-spin" />
            )}
            {!loading && query && (
              <button 
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-stone-100 rounded-full"
              >
                <X className="h-3 w-3 text-stone-400" />
              </button>
            )}
          </div>
        </div>
        
        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto">
          {/* Sugestões quando vazio */}
          {!query && (
            <div className="p-4">
              <p className="text-xs uppercase text-stone-400 font-medium mb-3">Sugestões de pesquisa</p>
              <div className="flex flex-wrap gap-2">
                {sugestoes.map((sugestao, idx) => (
                  <button
                    key={idx}
                    onClick={() => setQuery(sugestao)}
                    className="px-3 py-1.5 bg-stone-100 hover:bg-stone-200 rounded-full text-sm text-stone-700 transition-colors"
                  >
                    {sugestao}
                  </button>
                ))}
              </div>
              
              <p className="text-xs uppercase text-stone-400 font-medium mt-6 mb-3">Atalhos rápidos</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => { navigate('/gestor/dashboard'); onClose(); }}
                  className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-sm font-medium">Dashboard</span>
                </button>
                <button
                  onClick={() => { navigate('/gestor/ofs'); onClose(); }}
                  className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Package className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium">Ordens de Fabrico</span>
                </button>
                <button
                  onClick={() => { navigate('/gestor/operadores'); onClose(); }}
                  className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <User className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm font-medium">Operadores</span>
                </button>
                <button
                  onClick={() => { navigate('/gestor/performance'); onClose(); }}
                  className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium">Performance</span>
                </button>
              </div>
            </div>
          )}
          
          {/* Resultados */}
          {query && resultados.length > 0 && (
            <div className="p-2">
              {resultados.map((resultado, idx) => {
                const config = TIPO_CONFIG[resultado.tipo] || TIPO_CONFIG.link;
                const Icon = config.icon;
                const isSelected = idx === selectedIndex;
                
                return (
                  <button
                    key={idx}
                    onClick={() => irParaResultado(resultado)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left ${
                      isSelected ? 'bg-primary/5 ring-1 ring-primary/20' : 'hover:bg-stone-50'
                    }`}
                    data-testid={`busca-resultado-${idx}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-stone-900 truncate">{resultado.titulo}</span>
                        {resultado.dados?.em_atraso && (
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-medium rounded">
                            ATRASO
                          </span>
                        )}
                        {resultado.dados?.status && (
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                            resultado.dados.status === 'online' ? 'bg-green-100 text-green-700' :
                            resultado.dados.status === 'offline' ? 'bg-stone-100 text-stone-600' :
                            resultado.dados.status === 'ferias' ? 'bg-cyan-100 text-cyan-700' :
                            'bg-stone-100 text-stone-600'
                          }`}>
                            {resultado.dados.status.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-stone-500 truncate">{resultado.descricao}</p>
                      <p className="text-xs text-stone-400 mt-0.5">{resultado.origem}</p>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {resultado.dados?.progresso !== undefined && (
                        <div className="text-right">
                          <span className="text-sm font-medium text-stone-700">{resultado.dados.progresso}%</span>
                        </div>
                      )}
                      <ArrowRight className={`h-4 w-4 transition-colors ${isSelected ? 'text-primary' : 'text-stone-300'}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          
          {/* Sem resultados */}
          {query && !loading && resultados.length === 0 && (
            <div className="p-8 text-center">
              <Search className="h-12 w-12 mx-auto text-stone-200 mb-3" />
              <p className="text-stone-500">Nenhum resultado encontrado para "{query}"</p>
              <p className="text-sm text-stone-400 mt-1">Tente pesquisar por OFs, operadores, clientes ou palavras-chave</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-3 border-t bg-stone-50 flex items-center justify-between text-xs text-stone-400">
          <div className="flex items-center gap-4">
            <span><kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">↑</kbd> <kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">↓</kbd> para navegar</span>
            <span><kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">Enter</kbd> para abrir</span>
            <span><kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">Esc</kbd> para fechar</span>
          </div>
          <span>
            {resultados.length > 0 && `${resultados.length} resultado${resultados.length !== 1 ? 's' : ''}`}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
